import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import OptimizedImage from "./OptimizedImage";

const Header = ({
    phone = "+(260) 773 029344",
    email = "admissions@africaresearchuniversity.com",
    address = "Quadrant Office Park, Great East Road, Lusaka, Zambia",
    alumniLink = "/alumni",
}) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const location = useLocation();

    const [aboutOpen, setAboutOpen] = useState(false);
    const [resourcesOpen, setResourcesOpen] = useState(false);

    const navLinks = [
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
        { name: "Academic Programmes", path: "/courses" },
        { name: "Student Resources", path: "/student-resources" },
        { name: "News & Events", path: "/articles" },
        { name: "Payments", path: "/#" },
        { name: "Contact", path: "/contact" },
    ];

    const aboutSubLinks = [
        { name: "About Us", path: "/about" },
        { name: "Who We Are", path: "/who-we-are" },
        { name: "Admissions Policy", path: "/admissions" },
        { name: "Ethics Committee", path: "/ethics-committee" },
        { name: "Fee Structure", path: "/fee-structure" },
        { name: "Vacancies", path: "/jobs" },
    ];

    const paymentsSublinks = [
        { name: "E-Payments", path: "https://payments.africaresearchuniversity.com" }
    ];

    return (
        <header className="fixed top-0 left-0 w-full bg-white shadow z-50">
            {/* Contact bar */}
            <div className="w-full bg-green-600 text-gray-100 text-sm px-2">
                <div className="container mx-auto flex items-center justify-between p-2">
                    <div className="flex items-center space-x-4">
                        <a
                            href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                            className="flex items-center space-x-2 hover:text-gray-300"
                        >
                            <i className="bi bi-telephone-fill text-base" aria-hidden="true" />
                            <span className="hidden sm:inline">{phone}</span>
                        </a>
                        <a href={`mailto:${email}`} className="flex items-center space-x-2 hover:text-gray-300">
                            <i className="bi bi-envelope-fill text-base" aria-hidden="true" />
                            <span className="hidden sm:inline">{email}</span>
                        </a>
                    </div>

                    <div className="flex items-center space-x-4">
                        <a
                            href={alumniLink}
                            aria-label="Alumni"
                            className="bg-white/90 text-green-800 rounded-full px-3 py-1 flex items-center space-x-2 hover:bg-white focus:outline-none focus:ring-2 focus:ring-white cursor-pointer transition-shadow shadow-sm"
                        >
                            <i className="bi bi-mortarboard-fill text-base" aria-hidden="true" />
                            <span className="hidden sm:inline">Alumni</span>
                        </a>
                        <div className="hidden sm:block">
                            <i className="bi bi-pin-fill text-base" aria-hidden="true" /> {address}
                        </div>
                        {/* mobile toggle for contact details */}
                        <button
                            className="sm:hidden p-1"
                            aria-expanded={contactOpen}
                            aria-label="Toggle contact details"
                            onClick={() => setContactOpen(!contactOpen)}
                        >
                            <i className={`bi ${contactOpen ? "bi-chevron-up" : "bi-chevron-down"} text-lg`} aria-hidden="true" />
                        </button>
                    </div>
                </div>

                {contactOpen && (
                    <div className="sm:hidden container mx-auto px-4 pb-2">
                        <div className="flex flex-col space-y-1 text-sm">
                            <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="flex items-center space-x-2 hover:text-green-600">
                                <i className="bi bi-telephone-fill text-base" aria-hidden="true" />
                                <span>Phone: {phone}</span>
                            </a>

                            <a href={`mailto:${email}`} className="flex items-center space-x-2 hover:text-green-600">
                                <i className="bi bi-envelope-fill text-base" aria-hidden="true" />
                                <span>{email}</span>
                            </a>

                            <div className="flex items-center space-x-2">
                                <i className="bi bi-geo-alt-fill text-base" aria-hidden="true" />
                                <span>{address}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="container mx-auto flex items-center justify-between p-4">
                <Link to="/" className="flex items-center space-x-2">
                    <OptimizedImage src="/assets/img/arulogo.png" alt="Africa Research University logo - home page link" className="h-20 w-auto" width={80} height={80} />
                </Link>

                {/* Desktop Navigation */}
                <nav className="hidden md:flex space-x-6 items-center">
                    {navLinks.map((link) =>
                        link.name === "About" ? (
                            <div key="about" className="relative group">
                                <Link
                                    to={link.path}
                                    className={`hover:text-green-600 transition ${location.pathname === link.path ? "text-green-600 font-medium" : ""
                                        }`}
                                >
                                    {link.name}
                                </Link>

                                {/* Dropdown with hover persistence and scroll */}
                                <div
                                    className="absolute left-0 mt-2 w-48 bg-white text-gray-700 rounded shadow-lg z-50
                            opacity-0 invisible
                            group-hover:opacity-100 group-hover:visible
                            hover:opacity-100 hover:visible
                            transition-all duration-150"
                                >
                                    <ul className="py-2 max-h-60 overflow-y-auto">
                                        {aboutSubLinks.map((sublink) => (
                                            <li key={sublink.name}>
                                                <Link
                                                    to={sublink.path}
                                                    className="block px-4 py-2 hover:bg-gray-100"
                                                    onClick={() => setMobileOpen(false)}
                                                >
                                                    {sublink.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : link.name === "Student Resources" ? (
                            <Link
                                key="resources"
                                to={link.path}
                                className={`hover:text-green-600 transition ${location.pathname === link.path ? "text-green-600 font-medium" : ""}`}
                            >
                                {link.name}
                            </Link>
                        ) : link.name === "Payments" ? (
                            <div key="payments" className="relative group">
                                <Link
                                    to={link.path}
                                    className={`hover:text-green-600 transition ${location.pathname === link.path ? "text-green-600 font-medium" : ""
                                        }`}
                                >
                                    {link.name}
                                </Link>

                                <div
                                    className="absolute left-0 mt-2 w-64 bg-white text-gray-700 rounded shadow-lg z-50
                            opacity-0 invisible
                            group-hover:opacity-100 group-hover:visible
                            hover:opacity-100 hover:visible
                            transition-all duration-150"
                                >
                                    <ul className="py-2 max-h-60 overflow-y-auto">
                                        {paymentsSublinks.map((sublink) => (
                                            <li key={sublink.name}>
                                                <a
                                                    href={sublink.path}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block px-4 py-2 hover:bg-gray-100"
                                                    onClick={() => setMobileOpen(false)}
                                                >
                                                    {sublink.name}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <Link
                                key={link.name}
                                to={link.path}
                                className={`hover:text-green-600 transition ${location.pathname === link.path ? "text-green-600 font-medium" : ""
                                    }`}
                            >
                                {link.name}
                            </Link>
                        )
                    )}
                </nav>

                <Link
                    to="https://aruportal.com"
                    className="hidden md:inline-block bg-green-600 text-white px-4 py-2 hover:bg-green-500 transition rounded-lg"
                    aria-label="Student Portal"
                >
                    Student Portal
                </Link>

                {/* Mobile Menu Toggle */}
                <button className="md:hidden text-2xl" onClick={() => setMobileOpen(!mobileOpen)}>
                    <i className="bi bi-list"></i>
                </button>
            </div>
        </header>
    );
};

export default Header;
